import { OHIFImagingLabel } from '@dynamika-shared/types/imagingLabels';
import {
  CommonInventoryType,
  UploadFileTypeEnum,
} from '@dynamika-shared/types/inventory';
import { DXAReading } from '@dynamika-shared/types/DXAReading';
import { QCTVisitLabeledScore } from '@dynamika-shared/types/QCTReading';
import { DICOM_Series } from '@dynamika-shared/types/visits';

const EMPTY_LABEL_TEXT = '—';

export const normalizeLabelText = (value?: string | null): string =>
  value?.trim() || EMPTY_LABEL_TEXT;

export type VisitReadingSources = {
  dxaReadings?: DXAReading[];
  qctScores?: QCTVisitLabeledScore[];
};

const getDxaAcquisitionDate = (
  label: CommonInventoryType,
  dxaReadings?: DXAReading[]
): string | null | undefined => {
  if (label.dataType !== UploadFileTypeEnum.DXA) {
    return undefined;
  }

  return dxaReadings?.find(reading => reading.labelId === label.id)
    ?.acquisitionDate;
};

/** The QCT exam date stands in for the acquisition date of a QCT label. */
const getQctAcquisitionDate = (
  label: CommonInventoryType,
  qctScores?: QCTVisitLabeledScore[]
): string | null | undefined => {
  if (label.dataType !== UploadFileTypeEnum.QCT) {
    return undefined;
  }

  return qctScores?.find(score => score.labelId === label.id)?.data?.examDate;
};

/** Ids of the labels a stored QCT result has been saved under. */
export const selectAssignedQctLabelIds = (
  qctScores?: QCTVisitLabeledScore[]
): string[] =>
  (qctScores ?? []).flatMap(score => (score.labelId ? [score.labelId] : []));

export const mergeVisitLabel = (
  label: CommonInventoryType,
  assignedLabel?: DICOM_Series,
  { dxaReadings, qctScores }: VisitReadingSources = {}
): OHIFImagingLabel => ({
  id: label.id,
  name: normalizeLabelText(label.name),
  dataType: label.dataType,
  acquisitionDate:
    getDxaAcquisitionDate(label, dxaReadings) ??
    getQctAcquisitionDate(label, qctScores) ??
    assignedLabel?.acquisitionDate ??
    null,
  assignedToSeriesId: assignedLabel?.id ?? '',
  assignedToSeriesInstanceUId: assignedLabel?.seriesInstanceUid ?? '',
});
